import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, InspectorControls, RichText, MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import { PanelBody, SelectControl, Button } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

registerBlockType('puroclean/icon-and-title', {
    edit({ attributes, setAttributes }) {
        const { customSvgUrl, customSvgId, title, iconSize, textColor, gap, titleElement } = attributes;
        
        const blockProps = useBlockProps({
            className: 'icon-and-title-block'
        });

        // Determine if using custom SVG
        const useCustomSvg = !!(customSvgUrl && customSvgId);

        // Icon size mapping
        const getIconSizeClass = (size) => {
            switch (size) {
                case 'small': return 'w-8 h-8';
                case 'medium': return 'w-12 h-12';
                case 'large': return 'w-16 h-16';
                case 'extra-large': return 'w-20 h-20';
                default: return 'w-12 h-12';
            }
        };

        // Text color classes
        const getTextColorClass = (color) => {
            switch (color) {
                case 'white': return 'text-white';
                case 'black': return 'text-black';
                case 'primary': return 'text-primary';
                case 'secondary': return 'text-secondary-900';
                default: return 'text-white';
            }
        };

        // Gap classes
        const getGapClass = (gapSize) => {
            return `gap-${gapSize}`;
        };

        const currentIconSize = getIconSizeClass(iconSize);
        const currentTextColor = getTextColorClass(textColor);
        const currentGap = getGapClass(gap);

        return (
            <>
                <InspectorControls>
                    <PanelBody title={__('Icon Settings', 'puroclean')} initialOpen={true}>
                        
                        {/* Custom SVG Upload */}
                        <div style={{ marginBottom: '16px' }}>
                            <strong>{__('Upload SVG Icon', 'puroclean')}</strong>
                            <MediaUploadCheck>
                                <MediaUpload
                                    onSelect={(media) => {
                                        setAttributes({
                                            customSvgUrl: media.url,
                                            customSvgId: media.id
                                        });
                                    }}
                                    allowedTypes={['image']}
                                    value={customSvgId}
                                    render={({ open }) => (
                                        <div style={{ marginTop: '8px' }}>
                                            <Button
                                                onClick={open}
                                                variant={useCustomSvg ? 'secondary' : 'primary'}
                                            >
                                                {useCustomSvg ? __('Change Icon', 'puroclean') : __('Upload SVG Icon', 'puroclean')}
                                            </Button>
                                            {useCustomSvg && (
                                                <Button
                                                    onClick={() => setAttributes({ customSvgUrl: '', customSvgId: 0 })}
                                                    variant="tertiary"
                                                    isDestructive
                                                    style={{ marginLeft: '8px' }}
                                                >
                                                    {__('Remove', 'puroclean')}
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                />
                            </MediaUploadCheck>

                            {useCustomSvg && (
                                <div style={{ marginTop: '12px' }}>
                                    <img 
                                        src={customSvgUrl} 
                                        alt=""
                                        style={{ maxWidth: '100%', height: 'auto', maxHeight: '100px' }}
                                    />
                                </div>
                            )}
                        </div>
                        
                        <SelectControl
                            label={__('Icon Size', 'puroclean')}
                            value={iconSize}
                            options={[
                                { label: __('Small (32px)', 'puroclean'), value: 'small' },
                                { label: __('Medium (48px)', 'puroclean'), value: 'medium' },
                                { label: __('Large (64px)', 'puroclean'), value: 'large' },
                                { label: __('Extra Large (80px)', 'puroclean'), value: 'extra-large' }
                            ]}
                            onChange={(value) => setAttributes({ iconSize: value })}
                        />
                    </PanelBody>

                    <PanelBody title={__('Text Settings', 'puroclean')} initialOpen={true}>
                        <SelectControl
                            label={__('Title Element', 'puroclean')}
                            value={titleElement}
                            options={[
                                { label: __('Heading 2 (H2)', 'puroclean'), value: 'h2' },
                                { label: __('Heading 3 (H3)', 'puroclean'), value: 'h3' },
                                { label: __('Heading 4 (H4)', 'puroclean'), value: 'h4' },
                                { label: __('Heading 5 (H5)', 'puroclean'), value: 'h5' },
                                { label: __('Heading 6 (H6)', 'puroclean'), value: 'h6' },
                                { label: __('Paragraph (P)', 'puroclean'), value: 'p' }
                            ]}
                            onChange={(value) => setAttributes({ titleElement: value })}
                        />
                        
                        <SelectControl
                            label={__('Text Color', 'puroclean')}
                            value={textColor}
                            options={[
                                { label: __('White', 'puroclean'), value: 'white' },
                                { label: __('Black', 'puroclean'), value: 'black' },
                                { label: __('Primary', 'puroclean'), value: 'primary' },
                                { label: __('Secondary', 'puroclean'), value: 'secondary' }
                            ]}
                            onChange={(value) => setAttributes({ textColor: value })}
                        />
                    </PanelBody>

                    <PanelBody title={__('Layout Settings', 'puroclean')} initialOpen={false}>
                        <SelectControl
                            label={__('Gap Between Icon and Title', 'puroclean')}
                            value={gap}
                            options={[
                                { label: __('Small (8px)', 'puroclean'), value: '2' },
                                { label: __('Medium (16px)', 'puroclean'), value: '4' },
                                { label: __('Large (24px)', 'puroclean'), value: '6' },
                                { label: __('Extra Large (32px)', 'puroclean'), value: '8' }
                            ]}
                            onChange={(value) => setAttributes({ gap: value })}
                        />
                    </PanelBody>
                </InspectorControls>

                <div {...blockProps}>
                    {/* Editor Preview */}
                    <div className={`flex items-center ${currentGap} ${currentTextColor}`}>
                        {/* Icon Preview */}
                        <div className="flex-shrink-0">
                            {useCustomSvg ? (
                                <img 
                                    src={customSvgUrl} 
                                    alt=""
                                    className={`${currentIconSize}`}
                                    style={{ objectFit: 'contain' }}
                                />
                            ) : (
                                <div 
                                    className={`${currentIconSize} ${currentTextColor} flex items-center justify-center border-2 border-dashed border-gray-400 rounded`}
                                    style={{ 
                                        background: 'transparent',
                                        opacity: 0.7
                                    }}
                                >
                                    <span className="text-xs text-center leading-tight">
                                        {__('Upload SVG', 'puroclean')}
                                    </span>
                                </div>
                            )}
                        </div>
                        
                        {/* Title */}
                        <div className="flex-1">
                            <RichText
                                tagName={titleElement}
                                className={`font-semibold !my-0 ${currentTextColor}`}
                                value={title}
                                onChange={(value) => setAttributes({ title: value })}
                                placeholder={__('Enter title...', 'puroclean')}
                                allowedFormats={['core/bold', 'core/italic']}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    },
    save() {
        // Save function returns null because we're using render.php
        return null;
    }
}); 